/**
 * Payroll finalize, release, and the payslip and payment tracks of a released run (ADR 0010 section 9,
 * ADR 0014).
 *
 *     approved ──finalize──▶ finalized ──release──▶ released
 *                                                     ├── payslips: not_published → published
 *                                                     └── payments: not_started → pending → file_generated
 *                                                                    → submitted → paid | partially_paid | failed
 *
 * CONTRACT FIRST (CLAUDE.md invariant 8). This file declares the routes and their schemas; nothing
 * implements them yet.
 *
 * THE FOUR ACTIONS ARE FOUR PERMISSIONS, AND EACH IS HELD NARROWLY.
 *   - `finalize`  `payroll.run:finalize`  locks the approved calculation version, its results and its
 *                                          rules snapshot. Finance and the owner — the holders of
 *                                          `payroll.run:approve` — and never the people who prepare and submit
 *                                          a run.
 *   - `release`   `payroll.run:release`   makes a finalized run's outputs available. Same holders.
 *   - `publish`   `payslip:publish`       publishes payslips to employees. Payroll administration.
 *   - `payments`  `bank_file:generate`    records payment progress. Payroll administration and Finance.
 * Each route declares exactly ONE permission, so the copilot's tool list (which is filtered by it) never
 * offers finalize or release to a person who cannot perform them. Finalize, release, publish and payment
 * are writes: the copilot can only PROPOSE them, and they execute on the person's click as that person.
 *
 * EVERY WRITE TAKES `Idempotency-Key` (invariant 10) AND, WHERE IT MOVES A RUN, THE RUN'S `expectedVersion`.
 * A retry with the same key returns the original result; a second finalize with a different key is a 409.
 *
 * WHAT THIS DOES NOT DO. It generates no bank file, calls no bank or provider, produces no payslip PDF,
 * sends no email and opens no adjustment or correction run (ADR 0014, "What is NOT built"). A payment
 * status that says `file_generated`, `submitted` or `paid` is RECORDED BY A PERSON, with a reference,
 * and is never inferred.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { CURRENCIES } from '../enums.js';
import { PAYMENT_STATUSES, PAYSLIP_STATUSES } from '../payroll-lifecycle.js';
import {
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';
import { payslipPreviewSchema, runDetailSchema } from './payroll-runs.contract.js';


/**
 * Run steps reach the copilot through `payroll_run_step` (packages/ai), not as derived tools: each carries
 * `expectedVersion`, which a derived proposal freezes when it is made — so the second step of "lock and calculate" always
 * failed. The hand-written tool binds the version when the person approves. The screens call these routes as before.
 */
const RUN_STEP_AI = { ai: 'hidden' } as const;
const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });
const runParams = z.object({ runId: uuidSchema });
const currencySchema = z.enum(CURRENCIES);

export const payslipStatusSchema = z.enum(PAYSLIP_STATUSES);
export const paymentStatusSchema = z.enum(PAYMENT_STATUSES);

// ---------------------------------------------------------------------------
// Finalize and release
// ---------------------------------------------------------------------------

/**
 * Lock an APPROVED run. `expectedVersion` is the run's `version` the caller loaded; a run that has moved
 * on is a 409. The note is recorded on the finalization and is not shown to employees.
 */
export const finalizeRunBodySchema = z.object({
  expectedVersion: z.number().int().positive(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type FinalizeRunBody = z.infer<typeof finalizeRunBodySchema>;

/** Release a FINALIZED run. Per run (ADR 0014, D3). */
export const releaseRunBodySchema = z.object({
  expectedVersion: z.number().int().positive(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type ReleaseRunBody = z.infer<typeof releaseRunBodySchema>;

// ---------------------------------------------------------------------------
// The outputs of a released run
// ---------------------------------------------------------------------------

export const runOutputRowSchema = z.object({
  /** The `payroll_result_outputs` row. */
  id: uuidSchema,
  employmentId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string(),
  netPayable: decimalStringSchema,
  currency: currencySchema,
  /** A held salary is withheld from payment preparation; its payslip is unaffected. */
  isHeld: z.boolean(),
  /** Whether this row counts towards the run's payment status (not held, not zero-net). */
  isPayable: z.boolean(),
  payslipStatus: payslipStatusSchema,
  payslipPublishedAt: instantSchema.nullable(),
  paymentStatus: paymentStatusSchema,
  paymentStatusAt: instantSchema.nullable(),
  /** What the person who recorded the payment step said the file or transfer was. */
  paymentReference: z.string().nullable(),
  paymentNote: z.string().nullable(),
});
export type RunOutputRow = z.infer<typeof runOutputRowSchema>;

export const runOutputsQuerySchema = z.object({
  payslipStatus: payslipStatusSchema.optional(),
  paymentStatus: paymentStatusSchema.optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().min(5).max(100).default(25),
});

/** Employee counts per status, for the whole run — not just the page. */
const statusCountsSchema = z.object({
  payslips: z.object({
    not_published: z.number().int().nonnegative(),
    published: z.number().int().nonnegative(),
  }),
  payments: z.object({
    not_started: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    file_generated: z.number().int().nonnegative(),
    submitted: z.number().int().nonnegative(),
    paid: z.number().int().nonnegative(),
    partially_paid: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
  }),
  /** Rows that do not take part in the payment status: held, or zero net pay. */
  notPayable: z.number().int().nonnegative(),
});

export const runOutputsSchema = z.object({
  runId: uuidSchema,
  state: z.string(),
  payslipStatus: payslipStatusSchema,
  payslipsPublishedAt: instantSchema.nullable(),
  paymentStatus: paymentStatusSchema,
  paymentStatusAt: instantSchema.nullable(),
  counts: statusCountsSchema,
  items: z.array(runOutputRowSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type RunOutputs = z.infer<typeof runOutputsSchema>;

// ---------------------------------------------------------------------------
// Payslip publication
// ---------------------------------------------------------------------------

/**
 * Publish payslips of a RELEASED run. `employmentIds` omitted publishes every payslip that is not yet
 * published; supplied, it publishes exactly those (each must belong to the run). Publishing is monotonic:
 * an already-published payslip is left as it is, never re-published or withdrawn.
 */
export const publishPayslipsBodySchema = z.object({
  employmentIds: z.array(uuidSchema).min(1).max(5000).optional(),
});
export type PublishPayslipsBody = z.infer<typeof publishPayslipsBodySchema>;

export const publishPayslipsResultSchema = z.object({
  runId: uuidSchema,
  /** Payslips this request moved to `published`. */
  published: z.number().int().nonnegative(),
  /** Payslips in the request that were already published. */
  alreadyPublished: z.number().int().nonnegative(),
  payslipStatus: payslipStatusSchema,
  payslipsPublishedAt: instantSchema.nullable(),
});
export type PublishPayslipsResult = z.infer<typeof publishPayslipsResultSchema>;

// ---------------------------------------------------------------------------
// Payment progress
// ---------------------------------------------------------------------------

/** Every payment status a request may move TO: `not_started` is where a released run begins. */
export const PAYMENT_TARGET_STATUSES = PAYMENT_STATUSES.filter((s) => s !== 'not_started');

/** The targets that need a reference to the file or transfer, and the ones that need a reason. */
export const PAYMENT_TARGETS_NEEDING_REFERENCE = ['file_generated', 'submitted'] as const;
export const PAYMENT_TARGETS_NEEDING_REASON = ['failed', 'partially_paid'] as const;

/**
 * Record payment progress of a RELEASED run: one forward step of the payment machine, for every eligible
 * payable employee (`employmentIds` omitted) or for exactly those named. Each named employee must be able
 * to make that step or the whole request is a 409 and nothing moves; with none named, employees that cannot
 * make it (already further on, held, zero net) are skipped and counted, and a request that moves nobody is
 * a 409.
 *
 * NOTHING HERE PRODUCES A FILE. `file_generated` says a file EXISTS — one the company made itself — and
 * `reference` says which. `submitted` and `paid` are likewise reported, never detected.
 */
export const recordPaymentTransitionBodySchema = z
  .object({
    to: z.enum(PAYMENT_TARGET_STATUSES as [string, ...string[]]),
    employmentIds: z.array(uuidSchema).min(1).max(5000).optional(),
    reference: z.string().trim().min(1).max(200).nullable().optional(),
    note: z.string().trim().max(500).nullable().optional(),
  })
  .superRefine((b, ctx) => {
    if (
      (PAYMENT_TARGETS_NEEDING_REFERENCE as readonly string[]).includes(b.to) &&
      !b.reference?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reference'],
        message: `Say which file or transfer this is: '${b.to}' is recorded against a reference`,
      });
    }
    if ((PAYMENT_TARGETS_NEEDING_REASON as readonly string[]).includes(b.to) && !b.note?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['note'],
        message: `Say what happened: '${b.to}' is recorded with a reason`,
      });
    }
  });
export type RecordPaymentTransitionBody = z.infer<typeof recordPaymentTransitionBodySchema>;

export const paymentTransitionResultSchema = z.object({
  runId: uuidSchema,
  to: paymentStatusSchema,
  /** Employees this request moved. */
  moved: z.number().int().nonnegative(),
  /** Payable employees left where they were because they cannot make this step (only when none were named). */
  skipped: z.number().int().nonnegative(),
  paymentStatus: paymentStatusSchema,
  paymentStatusAt: instantSchema.nullable(),
  /** Set when the first step (`pending`) was recorded: the reopen rule already refused it, now so does the DB. */
  paymentInitiatedAt: instantSchema.nullable(),
});
export type PaymentTransitionResult = z.infer<typeof paymentTransitionResultSchema>;

// ---------------------------------------------------------------------------
// An employee's own payslips
// ---------------------------------------------------------------------------

/**
 * A payslip the employee may read: PUBLISHED, from a RELEASED run, and their own. It omits what is not on
 * an employee's own payslip — employer cost and a hold's reason — and is never a draft.
 */
export const myPayslipSchema = payslipPreviewSchema
  .omit({
    isDraft: true,
    draftReason: true,
    employerContributions: true,
    employerContributionTotal: true,
    isHeld: true,
    holdReason: true,
  })
  .extend({
    /** The `payroll_result_outputs` row: what to ask for. */
    id: uuidSchema,
    runId: uuidSchema,
    publishedAt: instantSchema,
  });
export type MyPayslip = z.infer<typeof myPayslipSchema>;

export const myPayslipSummarySchema = z.object({
  id: uuidSchema,
  runId: uuidSchema,
  periodLabel: z.string(),
  payDate: dateOnlySchema,
  netPayable: decimalStringSchema,
  currency: currencySchema,
  publishedAt: instantSchema,
});
export type MyPayslipSummary = z.infer<typeof myPayslipSummarySchema>;

export const myPayslipListSchema = z.object({ items: z.array(myPayslipSummarySchema) });
export type MyPayslipList = z.infer<typeof myPayslipListSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

/**
 * A byte-stable marker the drift tests read: the routes below are the ONLY new routes of ADR 0014.
 * Static segments (`finalize`, `release`, `payslips/publish`, `payments/transitions`, `outputs`) are
 * declared before any parameterised sibling.
 */
export const payrollLifecycleContract = c.router(
  {
    finalizeRun: {
      method: 'POST',
      path: '/runs/:runId/finalize',
      pathParams: runParams,
      headers: idempotencyHeadersSchema,
      body: finalizeRunBodySchema,
      responses: {
        200: runDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** The run moved on, is already finalized, or the person who submitted it is finalizing it. */
        409: errorBodySchema,
        /** Not approved, the approved version is not the current one, or an engine replay found a problem. */
        412: errorBodySchema,
      },
      summary: 'Lock an approved run as the final payroll result',
      metadata: routeMeta('payroll.run:finalize', { ...RUN_STEP_AI, salaryPin: 'others' }),
    },

    releaseRun: {
      method: 'POST',
      path: '/runs/:runId/release',
      pathParams: runParams,
      headers: idempotencyHeadersSchema,
      body: releaseRunBodySchema,
      responses: {
        200: runDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /** The run is not finalized. */
        412: errorBodySchema,
      },
      summary: 'Release a finalized run for payslip publication and payment',
      metadata: routeMeta('payroll.run:release', { ...RUN_STEP_AI, salaryPin: 'others' }),
    },

    listRunOutputs: {
      method: 'GET',
      path: '/runs/:runId/outputs',
      pathParams: runParams,
      query: runOutputsQuerySchema,
      responses: {
        200: runOutputsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "A released run's payslip and payment status, per run and per employee",
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    publishPayslips: {
      method: 'POST',
      path: '/runs/:runId/payslips/publish',
      pathParams: runParams,
      headers: idempotencyHeadersSchema,
      body: publishPayslipsBodySchema,
      responses: {
        200: publishPayslipsResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /** The run is not released. */
        412: errorBodySchema,
      },
      summary: 'Publish payslips of a released run to employees',
      metadata: routeMeta('payslip:publish', RUN_STEP_AI),
    },

    recordPaymentTransition: {
      method: 'POST',
      path: '/runs/:runId/payments/transitions',
      pathParams: runParams,
      headers: idempotencyHeadersSchema,
      body: recordPaymentTransitionBodySchema,
      responses: {
        200: paymentTransitionResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Nobody can make that step, or a named employee cannot. */
        409: errorBodySchema,
        /** The run is not released. */
        412: errorBodySchema,
      },
      summary: 'Record payment progress of a released run, per run or per employee',
      metadata: routeMeta('bank_file:generate'),
    },

    listMyPayslips: {
      method: 'GET',
      path: '/my-payslips',
      responses: {
        200: myPayslipListSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'My published payslips',
      metadata: routeMeta('payslip.own:read'),
    },

    getMyPayslip: {
      method: 'GET',
      path: '/my-payslips/:outputId',
      pathParams: z.object({ outputId: uuidSchema }),
      responses: {
        200: myPayslipSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One of my published payslips, with its breakdown',
      metadata: routeMeta('payslip.own:read'),
    },
  },
  { pathPrefix: '/payroll' },
);
